/**
 * Putting the catalogue in the order it should be offered in — the categories, and the interests
 * inside each one.
 *
 * The order is a business decision, not an alphabet: "Knettechnik" precedes "Ofentechnik" in the
 * dictionary, which says nothing about which of them a salesperson reaches for first. The backend
 * stores it as `sortIndex` on both levels and has no endpoint for a whole list, so a move here is
 * a handful of ordinary updates — which is why the two halves are separate functions: one
 * rearranges, the other says which rows that rearrangement actually changed.
 *
 * Written against the smallest shape the arithmetic needs — an id and a position — which is what
 * lets one set of functions serve both levels, and lets the tests skip building whole categories.
 */
export type Ordered = { id: string; sortIndex: number }

/** The list with `id` moved to the place `targetId` currently holds. */
export function moveTo<T extends Ordered>(list: T[], id: string, targetId: string): T[] {
  const from = list.findIndex((one) => one.id === id)
  const to = list.findIndex((one) => one.id === targetId)
  if (from < 0 || to < 0 || from === to) {
    return list
  }
  const next = [...list]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/**
 * The list with `id` moved one place up (`-1`) or down (`+1`).
 *
 * Off either end is the list unchanged rather than a wrap-around: the button is disabled there,
 * and a keyboard repeat that quietly teleported the top entry to the bottom would be worse than
 * nothing happening.
 */
export function moveBy<T extends Ordered>(list: T[], id: string, delta: -1 | 1): T[] {
  const from = list.findIndex((one) => one.id === id)
  const to = from + delta
  if (from < 0 || to < 0 || to >= list.length) {
    return list
  }
  return moveTo(list, id, list[to].id)
}

/**
 * The entries whose stored position no longer matches where they sit, each with the position it
 * has to be given — the list's own index, so the stored order comes out gapless and unambiguous.
 *
 * Only the ones that moved: a reorder of a dozen categories is two or three updates, not twelve,
 * and every update skipped is a write that cannot fail halfway.
 */
export function renumbered<T extends Ordered>(list: T[]): { entry: T; sortIndex: number }[] {
  return list
    .map((entry, sortIndex) => ({ entry, sortIndex }))
    .filter(({ entry, sortIndex }) => entry.sortIndex !== sortIndex)
}
